import React, { useState } from 'react';
import { getLogoUrl, getBucketUrl } from '../utils/bucketUrl';

/**
 * Smart component that matches manufacturer names to brand logos
 * Falls back to logo-dark.png if no match found
 * 
 * @param {Object} props
 * @param {string} props.manufacturer - Manufacturer name (e.g., "Honda", "Yamaha Motor")
 * @param {string} props.alt - Alt text (defaults to manufacturer name)
 * @param {string} props.className - CSS classes
 */
export default function BrandLogoMatcher({ manufacturer, alt, className = '' }) {
  const [imgSrc, setImgSrc] = useState(() => {
    if (!manufacturer) {
      return getBucketUrl('logo-dark.png');
    }
    
    // Normalize manufacturer name to slug
    const slug = slugify(manufacturer);
    
    // Try bucket PNG first (all logos are PNG now)
    return getLogoUrl(`${slug}.png`);
  });

  const [fallbackAttempts, setFallbackAttempts] = useState(0);

  const handleError = () => {
    if (!manufacturer) {
      setImgSrc(getBucketUrl('logo-dark.png'));
      return;
    }

    const slug = slugify(manufacturer);

    if (fallbackAttempts === 0) {
      // Try bucket SVG
      setFallbackAttempts(1);
      setImgSrc(getLogoUrl(`${slug}.svg`));
    } else {
      // Final fallback - all logos are in bucket now
      setImgSrc(getBucketUrl('logo-dark.png'));
    }
  };

  return (
    <img 
      src={imgSrc} 
      alt={alt || manufacturer || 'Brand logo'} 
      className={className}
      onError={handleError}
      style={{ maxWidth: '100%', maxHeight: '100%' }}
    />
  );
}

/**
 * Normalize manufacturer name to filename-safe slug
 * Examples:
 *   "Honda" -> "honda"
 *   "Yamaha Motor" -> "yamaha-motor"
 *   "Harley-Davidson" -> "harley-davidson"
 *   "MV Agusta" -> "mv-agusta"
 */
function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    // Replace spaces and special chars with hyphens
    .replace(/[\s_]+/g, '-')
    .replace(/[^\w\-]+/g, '-')
    // Remove duplicate hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');
}
